import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import ArrowMark from '@/components/ArrowMark'
import AnimatedNumber from '@/components/AnimatedNumber'
import SectionLabel from '@/components/SectionLabel'
import BluePlaceholder from '@/components/BluePlaceholder'
import ArchLineBg from '@/components/ArchLineBg'
import api, { imgSrc } from '@/api/index'

function Hero() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [time, setTime] = useState('--:--');
  const heroRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const d = new Date();
      const opts = { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kathmandu', hour12: false };
      setTime(new Intl.DateTimeFormat('en-GB', opts).format(d));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!heroRef.current) return;
      const r = heroRef.current.getBoundingClientRect();
      setMouse({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
    };
    const el = heroRef.current;
    if (el) el.addEventListener('mousemove', onMove);
    return () => { if (el) el.removeEventListener('mousemove', onMove); };
  }, []);

  return (
    <section ref={heroRef} style={{
      position: 'relative', minHeight: '100vh', overflow: 'hidden',
      background: 'radial-gradient(ellipse 100% 70% at 50% 100%, rgba(27,123,63,0.06), transparent 60%), var(--bg-0)',
    }}>
      {/* Blueprint backdrop with mountain silhouette */}
      <div className="hero-backdrop" style={{ position: 'absolute', inset: 0, opacity: 0.85 }}>
        <svg viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
          <defs>
            <pattern id="grid-fine" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(43,43,43,0.05)" strokeWidth="0.5" />
            </pattern>
            <pattern id="grid-coarse" width="200" height="200" patternUnits="userSpaceOnUse">
              <path d="M 200 0 L 0 0 0 200" fill="none" stroke="rgba(43,43,43,0.07)" strokeWidth="0.5" />
            </pattern>
            {/* Glass gradients */}
            <linearGradient id="hb-green" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(27,123,63,0.04)"/>
              <stop offset="45%" stopColor="rgba(27,123,63,0.13)"/>
              <stop offset="100%" stopColor="rgba(27,123,63,0.03)"/>
            </linearGradient>
            <linearGradient id="hb-warm" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(200,195,185,0.05)"/>
              <stop offset="45%" stopColor="rgba(230,226,218,0.15)"/>
              <stop offset="100%" stopColor="rgba(200,195,185,0.04)"/>
            </linearGradient>
            <linearGradient id="hb-reflect" x1="0.2" y1="0" x2="0.6" y2="1">
              <stop offset="0%" stopColor="rgba(255,255,255,0.20)"/>
              <stop offset="60%" stopColor="rgba(255,255,255,0)"/>
            </linearGradient>
            <linearGradient id="hb-reflect2" x1="0" y1="0" x2="0.3" y2="1">
              <stop offset="0%" stopColor="rgba(255,255,255,0.14)"/>
              <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
            </linearGradient>
          </defs>
          <rect width="1600" height="900" fill="url(#grid-fine)" />
          <rect width="1600" height="900" fill="url(#grid-coarse)" />

          {/* mountain range silhouette */}
          <path d="M0 720 L120 600 L240 660 L380 480 L520 600 L660 540 L820 360 L980 540 L1120 480 L1280 600 L1440 540 L1600 660 L1600 900 L0 900 Z"
            fill="rgba(217,217,214,0.85)" stroke="rgba(27,123,63,0.3)" strokeWidth="0.8" />
          <path d="M0 760 L80 700 L200 740 L340 620 L480 700 L620 660 L780 540 L940 660 L1080 600 L1240 700 L1400 660 L1600 740 L1600 900 L0 900 Z"
            fill="rgba(226,226,220,1)" />

          {/* ═══════════ MODERN BUILDINGS (left side) ═══════════ */}
          <g opacity="0.88">

            {/* ── Building B: Low-rise Brise-Soleil Office (far left ground) ── */}
            <rect x="0" y="565" width="148" height="195"
              fill="url(#hb-warm)" stroke="rgba(43,43,43,0.20)" strokeWidth="0.6" />
            <rect x="0" y="540" width="96" height="26"
              fill="rgba(27,123,63,0.09)" stroke="rgba(27,123,63,0.28)" strokeWidth="0.6" />
            {Array.from({length:7}).map((_,i) => (
              <rect key={i} x={8+i*20} y={570} width={12} height={185}
                fill="rgba(255,255,255,0.06)" stroke="rgba(43,43,43,0.09)" strokeWidth="0.3" />
            ))}
            {[608, 650, 692, 734].map(y => (
              <line key={y} x1={0} y1={y} x2={148} y2={y} stroke="rgba(43,43,43,0.10)" strokeWidth="0.4" />
            ))}
            <rect x="0" y="565" width="40" height="195" fill="url(#hb-reflect2)" stroke="none" />

            {/* ── Building A: Twisted Needle Tower ── */}
            <polygon points="155,760 242,760 264,172 177,172"
              fill="url(#hb-green)" stroke="rgba(27,123,63,0.42)" strokeWidth="0.9" />
            <polygon points="155,760 179,760 201,172 177,172"
              fill="url(#hb-reflect)" stroke="none" />
            <polygon points="177,172 264,172 252,130 189,130"
              fill="rgba(27,123,63,0.14)" stroke="rgba(27,123,63,0.50)" strokeWidth="0.8" />
            <line x1="205" y1="130" x2="219" y2="80" stroke="rgba(27,123,63,0.60)" strokeWidth="1.5" />
            <circle cx="219" cy="80" r="2.5" fill="rgba(27,123,63,0.65)" />
            {Array.from({length:16}).map((_,i) => {
              const t = i/16
              const xl = 155 + (177-155)*t
              const xr = 242 + (264-242)*t
              const y = 760 - t*(760-172)
              return <line key={i} x1={xl} y1={y} x2={xr} y2={y} stroke="rgba(43,43,43,0.11)" strokeWidth="0.4" />
            })}
            {[0.28, 0.52, 0.76].map((f,i) => (
              <line key={i}
                x1={155 + f*(242-155)} y1={760}
                x2={177 + f*(264-177)} y2={172}
                stroke="rgba(43,43,43,0.07)" strokeWidth="0.4" />
            ))}
            <line x1="242" y1="760" x2="264" y2="172" stroke="rgba(255,255,255,0.40)" strokeWidth="0.8" />

            {/* ── Building F: Stepped Art Deco Spire ── */}
            <rect x="272" y="620" width="192" height="140" fill="url(#hb-warm)" stroke="rgba(43,43,43,0.18)" strokeWidth="0.6" />
            <rect x="290" y="496" width="156" height="126" fill="url(#hb-warm)" stroke="rgba(43,43,43,0.18)" strokeWidth="0.6" />
            <rect x="308" y="382" width="120" height="116" fill="url(#hb-warm)" stroke="rgba(43,43,43,0.20)" strokeWidth="0.7" />
            <rect x="324" y="282" width="88"  height="102" fill="url(#hb-green)" stroke="rgba(27,123,63,0.28)" strokeWidth="0.7" />
            <rect x="338" y="200" width="60"  height="84"  fill="rgba(27,123,63,0.11)" stroke="rgba(27,123,63,0.35)" strokeWidth="0.7" />
            <rect x="350" y="148" width="36"  height="54"  fill="rgba(27,123,63,0.16)" stroke="rgba(27,123,63,0.45)" strokeWidth="0.8" />
            <line x1="368" y1="148" x2="368" y2="100" stroke="rgba(27,123,63,0.60)" strokeWidth="1.6" />
            <circle cx="368" cy="100" r="3" fill="rgba(27,123,63,0.65)" />
            {Array.from({length:3}).map((_,r) =>
              Array.from({length:8}).map((_,c) => (
                <rect key={`f1${r}${c}`} x={280+c*22} y={628+r*42} width={14} height={28}
                  fill="rgba(27,123,63,0.06)" stroke="rgba(43,43,43,0.07)" strokeWidth="0.25" />
              ))
            )}
            {Array.from({length:3}).map((_,r) =>
              Array.from({length:6}).map((_,c) => (
                <rect key={`f2${r}${c}`} x={298+c*24} y={504+r*38} width={16} height={24}
                  fill="rgba(27,123,63,0.06)" stroke="rgba(43,43,43,0.07)" strokeWidth="0.25" />
              ))
            )}
            {Array.from({length:2}).map((_,r) =>
              Array.from({length:4}).map((_,c) => (
                <rect key={`f3${r}${c}`} x={316+c*26} y={390+r*50} width={18} height={32}
                  fill="rgba(27,123,63,0.06)" stroke="rgba(43,43,43,0.07)" strokeWidth="0.25" />
              ))
            )}
            <rect x="272" y="620" width="36" height="140" fill="url(#hb-reflect2)" stroke="none" />
            <rect x="290" y="496" width="30" height="126" fill="url(#hb-reflect2)" stroke="none" />

            {/* ── Building C: The Shard (featured center-left) ── */}
            <polygon points="468,760 625,760 622,640 627,472 607,308 579,154 547,68 515,178 493,336 479,498 469,640"
              fill="url(#hb-warm)" stroke="rgba(43,43,43,0.26)" strokeWidth="0.9" />
            <polygon points="625,760 622,640 627,472 607,308 579,154 547,68"
              fill="rgba(255,255,255,0.07)" stroke="rgba(43,43,43,0.20)" strokeWidth="0.6" />
            <polygon points="468,760 469,640 479,498 493,336 515,178 547,68"
              fill="url(#hb-reflect)" stroke="none" />
            {Array.from({length:18}).map((_,i) => {
              const t = i/18
              const y = 760 - t*(760-68)
              const xl = 468 + (547-468)*t
              const xr = 625 + (547-625)*t
              return <line key={i} x1={xl} y1={y} x2={xr} y2={y} stroke="rgba(43,43,43,0.07)" strokeWidth="0.35" />
            })}
            {[0.22, 0.44, 0.66, 0.88].map((f,i) => (
              <line key={i}
                x1={468 + f*(547-468)} y1={760}
                x2={468 + f*(547-468) * 0.3 + 547 * 0.7} y2={68}
                stroke="rgba(43,43,43,0.06)" strokeWidth="0.35" />
            ))}
            <line x1="547" y1="68" x2="622" y2="640" stroke="rgba(43,43,43,0.18)" strokeWidth="0.7" />
            <line x1="547" y1="68" x2="627" y2="472" stroke="rgba(43,43,43,0.13)" strokeWidth="0.5" />

            {/* ── Building D: Parametric Undulating Tower ── */}
            <path d="M638,760 L638,365 Q647,258 661,196 Q673,144 667,196 Q661,258 665,365 L665,760 Z"
              fill="url(#hb-green)" stroke="rgba(27,123,63,0.32)" strokeWidth="0.8" />
            {Array.from({length:14}).map((_,i) => {
              const y = 718 - i*41
              const wave = Math.sin(i * 0.55) * 14
              const leftX  = 638 - 10 - wave
              const rightX = 665 + 10 + wave * 0.4
              return (
                <g key={i}>
                  <line x1={leftX} y1={y} x2={rightX} y2={y} stroke="rgba(27,123,63,0.35)" strokeWidth="0.9" />
                  <line x1={leftX} y1={y+5} x2={rightX} y2={y+5} stroke="rgba(0,0,0,0.05)" strokeWidth="0.5" />
                </g>
              )
            })}
            <line x1="638" y1="760" x2="638" y2="365" stroke="rgba(255,255,255,0.35)" strokeWidth="0.7" />

            {/* ── Building E: Twin Towers + Sky Bridge ── */}
            {/* Tower E1 — taller */}
            <rect x="676" y="182" width="108" height="578"
              fill="url(#hb-warm)" stroke="rgba(43,43,43,0.22)" strokeWidth="0.8" />
            <rect x="676" y="182" width="30" height="578" fill="url(#hb-reflect2)" stroke="none" />
            {Array.from({length:15}).map((_,r) =>
              [686, 709, 734, 756].map((x,c) => (
                <rect key={`e1${r}${c}`} x={x} y={192+r*38} width={17} height={24}
                  fill="rgba(27,123,63,0.07)" stroke="rgba(43,43,43,0.09)" strokeWidth="0.28" />
              ))
            )}
            <rect x="688" y="158" width="84" height="26"
              fill="rgba(27,123,63,0.13)" stroke="rgba(27,123,63,0.38)" strokeWidth="0.7" />
            <line x1="730" y1="158" x2="730" y2="118" stroke="rgba(27,123,63,0.55)" strokeWidth="1.4" />
            <circle cx="730" cy="118" r="2.5" fill="rgba(27,123,63,0.60)" />

            {/* Tower E2 — shorter */}
            <rect x="796" y="278" width="98" height="482"
              fill="url(#hb-warm)" stroke="rgba(43,43,43,0.18)" strokeWidth="0.6" />
            <rect x="796" y="278" width="26" height="482" fill="url(#hb-reflect2)" stroke="none" />
            {Array.from({length:12}).map((_,r) =>
              [804, 826, 850, 874].map((x,c) => (
                <rect key={`e2${r}${c}`} x={x} y={288+r*38} width={16} height={24}
                  fill="rgba(240,235,228,0.10)" stroke="rgba(43,43,43,0.08)" strokeWidth="0.25" />
              ))
            )}

            {/* Sky bridge E1–E2 */}
            <rect x="784" y="396" width="12" height="26"
              fill="rgba(27,123,63,0.20)" stroke="rgba(27,123,63,0.42)" strokeWidth="0.8" />
            <line x1="784" y1="406" x2="796" y2="406" stroke="rgba(43,43,43,0.20)" strokeWidth="0.4" />
            <line x1="784" y1="416" x2="796" y2="416" stroke="rgba(43,43,43,0.20)" strokeWidth="0.4" />

            {/* ── Tower crane (between F and C) ── */}
            <g stroke="rgba(43,43,43,0.42)" strokeWidth="0.6" fill="none">
              <rect x="560" y="360" width="14" height="400" fill="rgba(43,43,43,0.04)" stroke="rgba(43,43,43,0.28)" strokeWidth="0.5" />
              {Array.from({length:8}).map((_,i) => (
                <React.Fragment key={i}>
                  <line x1="560" y1={360+i*50} x2="574" y2={360+(i+1)*50} stroke="rgba(43,43,43,0.22)" strokeWidth="0.4" />
                  <line x1="574" y1={360+i*50} x2="560" y2={360+(i+1)*50} stroke="rgba(43,43,43,0.22)" strokeWidth="0.4" />
                </React.Fragment>
              ))}
              <line x1="567" y1="360" x2="763" y2="338" strokeWidth="1.2" stroke="rgba(43,43,43,0.45)" />
              <line x1="567" y1="360" x2="479" y2="376" stroke="rgba(43,43,43,0.38)" />
              <rect x="463" y="372" width="18" height="12" fill="rgba(43,43,43,0.12)" stroke="rgba(43,43,43,0.30)" strokeWidth="0.5" />
              <line x1="695" y1="342" x2="695" y2="498" strokeDasharray="3 3" strokeWidth="0.5" />
              <rect x="687" y="496" width="16" height="12" fill="rgba(27,123,63,0.10)" />
              <path d="M693 508 Q693 524 687 530 Q681 536 687 544" strokeWidth="0.6" fill="none" />
            </g>

          </g>

          {/* dashed crosshair following mouse */}
          <g style={{ transform: `translate(${mouse.x * 1600}px, ${mouse.y * 900}px)`, transition: 'transform 0.4s ease-out' }}>
            <line x1="-1600" y1="0" x2="1600" y2="0" stroke="rgba(27,123,63,0.22)" strokeWidth="0.5" strokeDasharray="2 6" />
            <line x1="0" y1="-900" x2="0" y2="900" stroke="rgba(27,123,63,0.22)" strokeWidth="0.5" strokeDasharray="2 6" />
            <circle cx="0" cy="0" r="12" fill="none" stroke="rgba(27,123,63,0.4)" strokeWidth="0.5" />
          </g>
        </svg>
      </div>

      {/* Logo watermark — soft mark on the right */}
      <div style={{ position: 'absolute', right: '-4%', top: '8%', width: '52%', maxWidth: 760, opacity: 0.06, pointerEvents: 'none', zIndex: 1 }} className="hero-mark">
        <img src="/assets/smt-logo-full.png" alt="" style={{ width: '100%', display: 'block' }} />
      </div>

      {/* particle motes */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {Array.from({ length: 20 }).map((_, i) => {
          const seed = i * 137.5;
          return (
            <span key={i} style={{
              position: 'absolute',
              left: `${(seed * 13) % 100}%`,
              top: `${(seed * 23) % 100}%`,
              width: 2, height: 2, borderRadius: '50%',
              background: i % 4 === 0 ? 'var(--accent)' : 'rgba(255,255,255,0.5)',
              opacity: 0.5, animation: `float ${6 + (i % 5)}s ease-in-out ${(i * 0.3)}s infinite alternate`,
            }} />
          );
        })}
      </div>
      <style>{`
        @keyframes float { from { transform: translateY(0); } to { transform: translateY(-30px); } }
        @keyframes pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
      `}</style>

      {/* Content */}
      <div className="container hero-container" style={{
        position: 'relative', zIndex: 2, paddingTop: 'var(--hero-top)', paddingBottom: 'clamp(48px, 6vw, 80px)',
        display: 'flex', flexDirection: 'column', minHeight: '100vh',
      }}>
        {/* Massive headline */}
        <div style={{ marginTop: 'auto', marginBottom: 'auto', paddingTop: 80 }}>
          <div className="reveal" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '7px 14px 7px 8px', background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 999, marginBottom: 32, animationDelay: '0.05s', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'var(--accent)', color: '#fff', borderRadius: 999, fontFamily: 'var(--text)', fontSize: 11, fontWeight: 600 }}>
              <span style={{ width: 5, height: 5, borderRadius: 999, background: '#fff' }} />
              Available
            </span>
            <span style={{ fontFamily: 'var(--text)', fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>
              Class C licensed · ISO 9001:2015 · 36 years building
            </span>
          </div>
          <h1 className="display reveal" style={{
            fontSize: 'clamp(48px, 7.2vw, 120px)',
            color: 'var(--fg-0)', maxWidth: 1200, lineHeight: 0.98,
            animationDelay: '0.1s',
            fontWeight: 600,
            letterSpacing: '-0.03em',
          }}>
            Engineering <span style={{ color: 'var(--accent)' }}>Nepal&rsquo;s</span> future,<br />
            <span style={{ color: 'var(--fg-1)', fontWeight: 400 }}>built to last.</span>
          </h1>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 60, marginTop: 48, alignItems: 'end' }} className="hero-grid reveal" style={{ animationDelay: '0.2s' }}>
            <p style={{ fontSize: 18, color: 'var(--fg-1)', maxWidth: 560, lineHeight: 1.6 }}>
              A multidisciplinary construction firm building Nepal&rsquo;s homes, schools, hospitals, offices and institutional landmarks since 1989. From individual residences to embassy compounds, every project ships with the same engineering standard.
            </p>
            <div className="hero-cta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/projects" className="btn btn-primary">
                See our work <span className="arrow-circle"><ArrowMark /></span>
              </Link>
              <Link to="/contact" className="btn btn-ghost">
                Contact Us
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom telemetry strip */}
        <div style={{
          marginTop: 80, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24,
          paddingTop: 24, borderTop: '1px solid var(--line-soft)',
        }} className="hero-stats">
          {[
            { v: 36, suf: '+', label: 'Years of building' },
            { v: 240, suf: '+', label: 'Projects delivered' },
            { v: 50, suf: '+', label: 'Active clients' },
            { v: 150, suf: '+', label: 'Skilled crew & trades' },
          ].map((s, i) => (
            <div key={i}>
              <div className="display" style={{ fontSize: 'clamp(32px, 4vw, 48px)', color: 'var(--fg-0)' }}>
                <AnimatedNumber value={s.v} suffix={s.suf} />
              </div>
              <div className="mono" style={{ marginTop: 8, color: 'var(--fg-2)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Scroll indicator */}
        <div style={{ position: 'absolute', bottom: 32, right: 48, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="mono" style={{ color: 'var(--fg-2)' }}>SCROLL</span>
          <div style={{ width: 40, height: 1, background: 'var(--fg-2)', position: 'relative', overflow: 'hidden' }}>
            <span style={{
              position: 'absolute', top: 0, left: 0, height: 1, width: 12, background: 'var(--accent)',
              animation: 'scrollHint 2s ease-in-out infinite',
            }} />
          </div>
        </div>
        <style>{`@keyframes scrollHint { 0% { transform: translateX(-12px); } 100% { transform: translateX(40px); } }`}</style>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-grid .hero-cta { margin-top: 24px; }
          .hero-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .hero-mark { display: none; }
        }
      `}</style>
    </section>
  );
}

function HomeAbout() {
  return (
    <section style={{ padding: 'var(--sec-pad) 0', borderTop: '1px solid var(--line-soft)' }}>
      <div className="container">
        <SectionLabel title="About the practice" count="Est. 1989" />
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 96, marginTop: 64, alignItems: 'start' }} className="home-about-grid">
          <div>
            <h2 className="display" style={{ fontSize: 'clamp(40px, 6vw, 84px)', color: 'var(--fg-0)' }}>
              The contractor Nepal&rsquo;s most demanding clients <span style={{ color: 'var(--accent)' }}>keep coming back to</span>.
            </h2>
            <p style={{ marginTop: 32, fontSize: 17, color: 'var(--fg-1)', lineHeight: 1.65, maxWidth: 620 }}>
              Founded in 1989, SMT Construction is a multidisciplinary builder serving private homeowners, schools, hospitals, NGOs, government bodies and foreign missions across Nepal. We bring the same care and engineering standard to a family residence as we do to an embassy compound &mdash; that is what keeps clients with us for decades.
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 40, flexWrap: 'wrap' }}>
              <Link to="/about" className="btn btn-ghost">
                Read the full story <span className="arrow-circle"><ArrowMark /></span>
              </Link>
            </div>
          </div>
          <div className="panel" style={{ padding: 32 }}>
            <div className="mono" style={{ color: 'var(--fg-3)' }}>CORE COMPETENCIES</div>
            <ul style={{ listStyle: 'none', marginTop: 24, display: 'flex', flexDirection: 'column' }}>
              {[
                ['Civil', 'Civil & structural'],
                ['Mech', 'Mechanical systems'],
                ['Elec', 'Electrical & low-voltage'],
                ['San', 'Sanitary & plumbing'],
                ['Reno', 'Renovation & retrofit'],
                ['Sec', 'Security upgrades'],
              ].map(([c, l], i) => (
                <li key={c} style={{
                  display: 'grid', gridTemplateColumns: '60px 1fr auto', alignItems: 'center', gap: 16,
                  padding: '14px 0', borderTop: i === 0 ? 'none' : '1px solid var(--line-soft)',
                }}>
                  <span className="mono" style={{ color: 'var(--accent)' }}>{c}</span>
                  <span style={{ fontSize: 14 }}>{l}</span>
                  <span className="mono" style={{ color: 'var(--fg-3)' }}>{String(i+1).padStart(2,'0')}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .home-about-grid { grid-template-columns: 1fr !important; gap: 48px !important; } }`}</style>
    </section>
  );
}

function ProjectSkeleton() {
  return (
    <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ height: 280, background: 'var(--bg-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
      <div style={{ padding: 22 }}>
        <div style={{ height: 12, width: '60%', background: 'var(--bg-2)', borderRadius: 6, marginBottom: 12, animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ height: 22, width: '85%', background: 'var(--bg-2)', borderRadius: 6, marginBottom: 10, animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ height: 12, width: '100%', background: 'var(--bg-2)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.45} }`}</style>
    </div>
  );
}

function HomeProjects({ projects, loading, totalCount }) {
  return (
    <section style={{ padding: 'var(--sec-pad) 0', borderTop: '1px solid var(--line-soft)', background: 'var(--bg-1)' }}>
      <div className="container">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 64, flexWrap: 'wrap', gap: 24 }}>
          <div>
            <div className="mono" style={{ color: 'var(--accent)' }}>Selected works</div>
            <h2 className="display" style={{ fontSize: 'clamp(48px, 7vw, 96px)', marginTop: 24, color: 'var(--fg-0)' }}>
              Recent commissions.
            </h2>
          </div>
          {totalCount > 0 && (
            <Link to="/projects" className="btn btn-ghost">
              All projects ({totalCount}) <span className="arrow-circle"><ArrowMark /></span>
            </Link>
          )}
        </div>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }} className="featured-grid">
            {[0, 1, 2].map(i => <ProjectSkeleton key={i} />)}
          </div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--fg-3)' }}>
            <div className="mono" style={{ fontSize: 13 }}>No featured projects yet</div>
            <p style={{ marginTop: 8, fontSize: 14, color: 'var(--fg-2)' }}>Mark projects as featured in the admin panel to show them here.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }} className="featured-grid">
            {projects.map((p) => (
              <article key={p._id} className="panel lift" style={{
                padding: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column',
              }} onClick={() => { window.location.href = '/projects'; }}>
                <div style={{ position: 'relative' }}>
                  <BluePlaceholder label="" sub={p.year} variant={p.variant || 'building'} src={imgSrc(p.img)} height={280} />
                  <div style={{ position: 'absolute', top: 14, left: 14, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: p.status === 'ongoing' ? 'rgba(27,123,63,0.12)' : 'rgba(255,255,255,0.92)', color: p.status === 'ongoing' ? 'var(--accent-deep)' : 'var(--fg-1)', border: '1px solid', borderColor: p.status === 'ongoing' ? 'rgba(27,123,63,0.25)' : 'var(--line-soft)', fontFamily: 'var(--text)', fontSize: 11, fontWeight: 600 }}>
                    {p.status === 'ongoing' && <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />}
                    {p.status === 'ongoing' ? 'Ongoing' : 'Completed'}
                  </div>
                </div>
                <div style={{ padding: 22, display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--fg-2)', fontSize: 12 }}>
                    <span style={{ fontWeight: 500 }}>{p.category}</span>
                    <span>{p.location}</span>
                  </div>
                  <h3 className="display" style={{ fontSize: 22, marginTop: 12, color: 'var(--fg-0)', fontWeight: 600 }}>{p.title}</h3>
                  <p style={{ marginTop: 10, fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.55, flex: 1 }}>{p.scope || p.brief}</p>
                  <div style={{ marginTop: 14, fontSize: 12, color: 'var(--fg-2)' }}>For <span style={{ color: 'var(--fg-0)', fontWeight: 500 }}>{p.client}</span></div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <style>{`@media (max-width: 1100px) { .featured-grid { grid-template-columns: 1fr 1fr !important; } }
              @media (max-width: 720px) { .featured-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function HomeProcess() {
  const steps = [
    { n: '01', code: 'CON', title: 'Concept', body: 'Brief, feasibility, site survey, budget envelope.' },
    { n: '02', code: 'PLN', title: 'Planning', body: 'Permits, scheduling, supply chain, mobilisation.' },
    { n: '03', code: 'ENG', title: 'Engineering', body: 'Structural, MEP, BIM coordination, value engineering.' },
    { n: '04', code: 'BLD', title: 'Construction', body: 'Self-perform civil, MEP integration, QA/QC.' },
    { n: '05', code: 'DEL', title: 'Delivery', body: 'Commissioning, handover, 5-year defect liability.' },
  ];
  return (
    <section style={{ padding: 'var(--sec-pad) 0', borderTop: '1px solid var(--line-soft)' }}>
      <div className="container">
        <SectionLabel title="How we deliver" count="5 phases" />
        <h2 className="display" style={{ fontSize: 'clamp(40px, 6vw, 80px)', marginTop: 40, color: 'var(--fg-0)', maxWidth: 900 }}>
          A repeatable, auditable path from <span style={{ color: 'var(--accent)' }}>brief to handover</span>.
        </h2>
        <div style={{ marginTop: 80, position: 'relative' }}>
          {/* connecting line */}
          <div style={{ position: 'absolute', top: 28, left: 0, right: 0, height: 1, background: 'var(--line)' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 24 }} className="process-grid">
            {steps.map((s, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%', border: '1px solid var(--line)',
                  background: 'var(--bg-0)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', position: 'relative', zIndex: 2,
                }}>{s.n}</div>
                <div className="mono" style={{ marginTop: 24, color: 'var(--fg-3)' }}>{s.code}</div>
                <h3 className="display" style={{ fontSize: 24, marginTop: 8, color: 'var(--fg-0)' }}>{s.title}</h3>
                <p style={{ marginTop: 12, fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.55 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .process-grid { grid-template-columns: 1fr 1fr !important; } }
              @media (max-width: 540px) { .process-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function HomeClients({ clients, loading }) {
  if (loading || clients.length === 0) return null;
  const doubled = [...clients, ...clients];
  return (
    <section style={{ padding: '80px 0', borderTop: '1px solid var(--line-soft)', borderBottom: '1px solid var(--line-soft)', background: 'var(--bg-0)', overflow: 'hidden' }}>
      <div className="container">
        <div className="mono" style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--fg-3)', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
          <span>Trusted by</span>
          <span>{clients.length}+ private, public &amp; international clients</span>
        </div>
      </div>
      <div className="marquee-track" style={{ display: 'flex', overflow: 'hidden', mask: 'linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)', WebkitMask: 'linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)' }}>
        <div style={{ display: 'flex', gap: 64, animation: 'marquee 50s linear infinite', minWidth: 'max-content' }}>
          {doubled.map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0', whiteSpace: 'nowrap' }}>
              <span style={{ width: 8, height: 8, background: 'var(--accent)' }} />
              <span style={{ fontFamily: 'var(--space)', fontSize: 22, color: 'var(--fg-1)', fontWeight: 500 }}>{c.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeCTA() {
  return (
    <section style={{ padding: 'var(--sec-pad) 0 80px', position: 'relative', overflow: 'hidden' }}>
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{
          position: 'relative', borderRadius: 24, overflow: 'hidden',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F0EBE2 100%)',
          border: '1px solid var(--line-soft)',
          padding: 'clamp(48px, 7vw, 96px)',
          boxShadow: '0 30px 60px -30px rgba(0,0,0,0.12)',
        }}>
          {/* Architectural line drawing background */}
          <ArchLineBg variant="building" opacity={0.7} />
          {/* Logo watermark */}
          <div style={{ position: 'absolute', right: '-6%', top: '-10%', width: '40%', maxWidth: 480, opacity: 0.05, pointerEvents: 'none' }}>
            <img src="/assets/smt-logo-full.png" alt="" style={{ width: '100%', display: 'block' }} />
          </div>

          <div style={{ position: 'relative', zIndex: 2, display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 64, alignItems: 'center' }} className="cta-grid">
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'var(--accent-soft)', borderRadius: 999 }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-deep)' }}>Currently taking projects for 2026</span>
              </div>
              <h2 className="display" style={{ fontSize: 'clamp(38px, 5.5vw, 72px)', color: 'var(--fg-0)', marginTop: 24, lineHeight: 1.02, fontWeight: 600, letterSpacing: '-0.02em', maxWidth: 700 }}>
                Have a building in mind? <span style={{ color: 'var(--accent)' }}>Let&rsquo;s talk.</span>
              </h2>
              <p style={{ marginTop: 24, fontSize: 18, color: 'var(--fg-1)', lineHeight: 1.55, maxWidth: 560 }}>
                Tell us about your site, your budget and your timeline. We&rsquo;ll come back to you within one working day with honest feedback &mdash; no pressure, no sales pitch.
              </p>
              <div style={{ display: 'flex', gap: 12, marginTop: 36, flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn btn-primary">
                  Start a project <span className="arrow-circle"><ArrowMark /></span>
                </Link>
                <a href="tel:+97714599344" className="btn btn-ghost">
                  <span>Call +977 1 4599344</span>
                </a>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} className="cta-side">
              {[
                { label: 'Visit our office', value: 'Tanka Prasad Marg', sub: 'Baneshwor Height-10, Kathmandu' },
                { label: 'Send a brief', value: 'info@smtconstruction.com', sub: 'Response within one working day' },
                { label: 'Office hours', value: 'Sun – Fri · 9:00 – 18:00', sub: 'Nepal Standard Time' },
              ].map((row, i) => (
                <div key={i} style={{ background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 14, padding: '18px 20px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{row.label}</div>
                  <div style={{ fontFamily: 'var(--space)', fontSize: 16, fontWeight: 600, color: 'var(--fg-0)', marginTop: 6 }}>{row.value}</div>
                  <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 4 }}>{row.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .cta-grid { grid-template-columns: 1fr !important; gap: 32px !important; } }`}</style>
    </section>
  );
}

export default function HomePage() {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [allProjectsCount, setAllProjectsCount] = useState(0);
  const [landingClients, setLandingClients] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingClients, setLoadingClients] = useState(true);

  useEffect(() => {
    api.get('/projects?featured=true&limit=3')
      .then(({ data }) => setFeaturedProjects(data.data))
      .finally(() => setLoadingProjects(false));

    api.get('/projects')
      .then(({ data }) => setAllProjectsCount(data.data.length))
      .catch(() => {});

    api.get('/clients?landingPage=true')
      .then(({ data }) => setLandingClients(data.data))
      .finally(() => setLoadingClients(false));
  }, []);

  return (
    <div className="page">
      <Hero />
      <HomeAbout />
      <HomeProjects projects={featuredProjects} loading={loadingProjects} totalCount={allProjectsCount} />
      <HomeProcess />
      <HomeClients clients={landingClients} loading={loadingClients} />
      <HomeCTA />
    </div>
  );
}
